import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useEffect, useRef, useState } from 'react';

// Count Component
const SpecialtyCount = ({ asyncFunction }) => {
  const [specialtyData, setSpecialtyData] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        const data = await asyncFunction();
        setSpecialtyData(data);
      })()
    }
  }, [asyncFunction]);

  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm text-gray-600">
        {specialtyData ? specialtyData.length : '...'} total items across our platform!
      </span>
      <span className="text-sm">🎉</span>
    </div>
  );
};

const COLORS = ["#8B7ED8", "#A78BFA", "#C4B5FD", "#DDD6FE", "#EDE9FE", "#F3F0FF", "#B794F6", "#9F7AEA", "#805AD5", "#6B46C1"];

const defaultValueFormatter = (value) =>
  typeof value === "number" ? value.toLocaleString("en-US") : value ?? "—";

// Custom Tooltip for Pie Chart
const CustomTooltip = ({ active, payload, total, valueFormatter, valueLabel, percentageClassName }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : '0.0';
    const formattedValue = valueFormatter(data.value, data);
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-ktx-navy">{data.name}</p>
        <p className="text-sm text-sage-green">
          <span className="font-medium">{valueLabel}:</span> {formattedValue}
        </p>
        <p className={`text-sm ${percentageClassName || "text-ktx-purple"}`}>
          <span className="font-medium">Percentage:</span> {percentage}%
        </p>
      </div>
    );
  }
  return null;
};

// Custom Legend Component
const DEFAULT_VISIBLE_COUNT = 5;

const CustomLegend = ({
  data,
  total,
  valueFormatter,
  visibleCount,
  colors,
  percentageClassName,
  legendDropdownClassName,
  onItemSelect,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const hasData = Array.isArray(data) && data.length > 0;
  
  const handleItemSelect = (item) => {
    if (!onItemSelect) return;
    onItemSelect(item.name);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current?.contains(target) || toggleRef.current?.contains(target)) {
        return;
      }
      setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  if (!hasData) return null;
  const palette = Array.isArray(colors) && colors.length ? colors : COLORS;

  // Sort data by value in descending order
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  const limit = typeof visibleCount === "number" ? visibleCount : DEFAULT_VISIBLE_COUNT;
  const topItems = sortedData.slice(0, limit);
  const remaining = sortedData.slice(limit);

  const renderLegendItem = (item, index, isTopThree = true, enableHover = true, compact = false) => {
    const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
    const originalIndex = data.findIndex(d => d.name === item.name);
    const isTopSpecialty = index === 0;
    
    // Calculate bar width based on percentage of total
    const barWidth = total > 0 ? (item.value / total) * 100 : 0;
    
    return (
      <button
        key={index}
        type="button"
        onClick={onItemSelect ? () => handleItemSelect(item) : undefined}
        className={`w-full flex items-center space-x-2 p-1.5 rounded-md transition-all duration-200 ${
          onItemSelect
            ? enableHover
              ? "cursor-pointer hover:-translate-y-[1px] hover:shadow-sm"
              : "cursor-pointer"
            : "cursor-default"
        } ${
          isTopSpecialty
            ? 'bg-gradient-to-r from-green-100 to-sage-green/10 border border-sage-green/30 shadow-sm'
            : isTopThree
            ? 'bg-sage-green/10 border border-sage-green/20'
            : 'bg-gray-50 hover:bg-sage-green/5'
        }`}
      >
        {/* Color indicator with size-based styling */}
        <div 
          className={`rounded-full flex-shrink-0 ${
            isTopSpecialty ? 'w-3 h-3 ring-1 ring-purple-300' : 
            isTopThree ? 'w-2.5 h-2.5 ring-1 ring-purple-200' : 'w-2 h-2'
          }`}
          style={{ backgroundColor: palette[originalIndex % palette.length] }}
        ></div>
        
        {/* Specialty name with size-based typography */}
        <div className="flex-1 text-left min-w-0">
          <p className={`truncate ${
            isTopSpecialty 
              ? 'text-xs font-bold text-gray-800' 
              : isTopThree
              ? 'text-xs font-semibold text-gray-700'
              : 'text-xs font-medium text-gray-700'
          }`} title={item.name}>
            {item.name}
          </p>
        </div>
        
        {/* Visual bar representation */}
        {!compact && (
          <div className="flex-1 max-w-16">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="h-1.5 rounded-full transition-all duration-500"
                style={{
                  width: `${barWidth}%`,
                  backgroundColor: palette[originalIndex % palette.length],
                  opacity: isTopSpecialty ? 1 : isTopThree ? 0.85 : 0.7,
                }}
              ></div>
            </div>
          </div>
        )}
        
        {/* Value and percentage with size-based styling */}
        <div className="text-right">
          <p className={`${
            isTopSpecialty 
              ? 'text-xs font-bold text-gray-800' 
              : isTopThree
              ? 'text-xs font-semibold text-gray-700'
              : 'text-xs font-medium text-gray-700'
          }`}>
            {valueFormatter(item.value, item)}
          </p>
          {!compact && (
            <p className={`text-xs ${
              isTopSpecialty ? 'text-gray-600 font-medium' : 
              isTopThree ? 'text-gray-500' : 'text-gray-500'
            } ${percentageClassName || ""}`}>
              {percentage}%
            </p>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="mt-0 space-y-0.5">
      {/* Top items */}
      {topItems.map((item, index) => renderLegendItem(item, index, true, true))}
      
      {/* Dropdown for remaining specialties */}
      {remaining.length > 0 && (
        <div className="relative">
          <button
            ref={toggleRef}
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full flex items-center justify-between p-1.5 rounded-md bg-gray-100 hover:bg-sage-green/5 border border-gray-200 transition-all duration-200"
          >
            <span className="text-xs font-medium text-gray-700">
              +{remaining.length} more items
            </span>
            <svg 
              className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${
                showDropdown ? 'rotate-180' : ''
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Dropdown content */}
          {showDropdown && (
            <div
              ref={dropdownRef}
              className={`absolute left-0 right-0 bottom-full mb-1 space-y-1 max-h-72 overflow-y-auto border border-gray-200 rounded-md shadow-lg z-10 ${
                legendDropdownClassName || "bg-white"
              }`}
            >
              {remaining.map((item, index) => (
                <div key={index}>
                  {renderLegendItem(item, index + limit, false, false, true)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { SpecialtyCount };

export default function RevOpsPie({
  asyncFunction,
  legendAsyncFunction,
  valueFormatter = defaultValueFormatter,
  valueLabel = "Count",
  visibleCount = DEFAULT_VISIBLE_COUNT,
  messageClassName = "mt-2",
  rightColumn,
  colors,
  percentageClassName,
  legendDropdownClassName,
  onItemSelect,
  showUnpriced = false,
}) {
  const [dynamicPieData, setDynamicPieData] = useState(null);
  const [dynamicLegendData, setDynamicLegendData] = useState(null);
  const [showUnpricedModal, setShowUnpricedModal] = useState(false);
  const containerRef = useRef(null);
  const [columnSizes, setColumnSizes] = useState([30, 40, 30]);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    if (asyncFunction) {
      (async() => {
        setDynamicPieData(await asyncFunction());
      })()
    }
  }, [])

  useEffect(() => {
    if (legendAsyncFunction) {
      (async() => {
        setDynamicLegendData(await legendAsyncFunction());
      })()
    }
  }, [legendAsyncFunction])

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const updateMatch = (event) => setIsLargeScreen(event.matches);
    setIsLargeScreen(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateMatch);
      return () => mediaQuery.removeEventListener("change", updateMatch);
    }
    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  const sanitizedPieData = dynamicPieData
    ? dynamicPieData.filter((item) => {
        const name = item?.name?.toLowerCase();
        return name !== "n/a" && name !== "unknown" && name !== "custom";
      })
    : null;
  const filteredPieData = sanitizedPieData
    ? sanitizedPieData.filter((item) => {
        const name = item?.name?.toLowerCase();
        return name !== "unpriced" && !item?.isUnpriced && !item?.isUnknown;
      })
    : null;

  // Calculate total from the filtered dataset
  const baseLegendData = dynamicLegendData || dynamicPieData;
  const sanitizedLegendData = baseLegendData
    ? baseLegendData.filter((item) => {
        const name = item?.name?.toLowerCase();
        return name !== "n/a" && name !== "unknown" && name !== "custom";
      })
    : null;
  const filteredLegendData = sanitizedLegendData
    ? sanitizedLegendData.filter((item) => {
        const name = item?.name?.toLowerCase();
        return name !== "unpriced" && !item?.isUnpriced && !item?.isUnknown;
      })
    : null;
  const legendData = filteredLegendData?.length ? filteredLegendData : sanitizedLegendData;
  const pieData = filteredLegendData?.length
    ? filteredLegendData
    : sanitizedLegendData?.length
    ? sanitizedLegendData
    : filteredPieData?.length
    ? filteredPieData
    : sanitizedPieData;
  const total = pieData ? pieData.reduce((sum, item) => sum + item.value, 0) : 0;
  const legendTotal = legendData ? legendData.reduce((sum, item) => sum + item.value, 0) : 0;
  const infoData = Array.isArray(baseLegendData) ? baseLegendData : dynamicPieData;
  const unpricedLegendItems = Array.isArray(infoData)
    ? infoData.filter((item) => item?.isUnpriced || item?.name?.toLowerCase() === "unpriced")
    : [];
  const hasExactUnknown = Array.isArray(infoData)
    ? infoData.some((item) => String(item?.name || "").trim() === "Unknown")
    : false;
  const hasExactUnpriced = Array.isArray(infoData)
    ? infoData.some((item) => String(item?.name || "").trim() === "Unpriced")
    : false;
  const unknownCount = Array.isArray(infoData)
    ? infoData
        .filter((item) => {
          const name = String(item?.name || "").trim();
          if (hasExactUnknown) return name === "Unknown";
          return name.toLowerCase() === "unknown";
        })
        .reduce((sum, item) => sum + item.value, 0)
    : 0;
  const unpricedCount = Array.isArray(infoData)
    ? infoData
        .filter((item) => {
          const name = String(item?.name || "").trim();
          if (item?.isUnpriced) return true;
          if (hasExactUnpriced) return name === "Unpriced";
          return name.toLowerCase() === "unpriced";
        })
        .reduce((sum, item) => sum + item.value, 0)
    : 0;
  const filterParts = [];
  if (showUnpriced && unpricedCount > 0) {
    filterParts.push({ key: "Unpriced", label: "Unpriced", count: unpricedCount, linkable: true });
  }
  if (unknownCount > 0) filterParts.push({ key: "Unknown", label: "Unknown", count: unknownCount, linkable: true });
  const showFilterMessage = filterParts.length > 0;
  const filterMessage = showFilterMessage ? (
    <div
      className={`${messageClassName} max-w-[320px] w-full mr-auto text-right`.trim()}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-cyan-600">
        Needs more information
      </div>
      <div className="mt-1 space-y-0.5 text-xs text-slate-500">
        {filterParts.map((part) => (
          <div key={part.key}>
            {part.linkable && (part.key === "Unpriced" || onItemSelect) ? (
              <button
                type="button"
                onClick={() => {
                  if (part.key === "Unpriced") {
                    setShowUnpricedModal(true);
                    return;
                  }
                  onItemSelect(part.label);
                }}
                className="font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
              >
                {part.label.toLowerCase()}
              </button>
            ) : (
              <span className="font-medium text-slate-600">{part.label.toLowerCase()}</span>
            )}
            <span className="text-slate-400">: {part.count}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;
  const palette = Array.isArray(colors) && colors.length ? colors : COLORS;

  const startResize = (handleIndex, event) => {
    if (!isLargeScreen || !containerRef.current) return;
    event.preventDefault();
    const startX = event.clientX;
    const startSizes = [...columnSizes];
    const containerWidth = containerRef.current.getBoundingClientRect().width || 1;
    const minSize = 20;

    const onMove = (moveEvent) => {
      const deltaPercent = ((moveEvent.clientX - startX) / containerWidth) * 100;
      if (handleIndex === 0) {
        const rightSize = startSizes[2];
        const maxLeft = 100 - rightSize - minSize;
        const nextLeft = Math.min(maxLeft, Math.max(minSize, startSizes[0] + deltaPercent));
        const nextMiddle = 100 - rightSize - nextLeft;
        setColumnSizes([nextLeft, nextMiddle, rightSize]);
        return;
      }
      const leftSize = startSizes[0];
      const maxMiddle = 100 - leftSize - minSize;
      const nextMiddle = Math.min(maxMiddle, Math.max(minSize, startSizes[1] + deltaPercent));
      const nextRight = 100 - leftSize - nextMiddle;
      setColumnSizes([leftSize, nextMiddle, nextRight]);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleSliceClick = (entry) => {
    if (!onItemSelect) return;
    const name = entry?.name ?? entry?.payload?.name;
    if (name) onItemSelect(name);
  };
  
  return (
    <>
      {asyncFunction && pieData ? (
        <>
          <div
            ref={containerRef}
            className="group w-full relative flex flex-col lg:flex-row gap-4 items-start min-w-0"
          >
          <div
            className="relative w-full min-w-0"
            style={isLargeScreen ? { width: `${columnSizes[0]}%` } : undefined}
          >
            <ResponsiveContainer width="100%" height={270}>
              <PieChart margin={{ top: 10, right: 2, bottom: 20, left: 2 }}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius="75%"
                  fill="#8884d8"
                  dataKey="value"
                  stroke="transparent"
                  strokeWidth={0}
                  onClick={handleSliceClick}
                >
                  {pieData.map((_, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={palette[i % palette.length]}
                      style={onItemSelect ? { cursor: "pointer" } : undefined}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomTooltip
                      total={total}
                      valueFormatter={valueFormatter}
                      valueLabel={valueLabel}
                      percentageClassName={percentageClassName}
                    />
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            {onItemSelect && (
              <div className="pointer-events-none absolute right-3 top-3 text-[11px] font-medium text-slate-500 underline-offset-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                Click to view details
              </div>
            )}
            {rightColumn && (
              <button
                type="button"
                onMouseDown={(event) => startResize(0, event)}
                className="hidden lg:block absolute top-0 right-0 h-full w-2 cursor-col-resize bg-transparent hover:bg-slate-200/60"
                aria-label="Resize chart panel"
              />
            )}
          </div>
          <div
            className="relative w-full min-w-0"
            style={isLargeScreen ? { width: `${columnSizes[1]}%` } : undefined}
          >
            <CustomLegend
              data={legendData}
              total={legendTotal}
              valueFormatter={valueFormatter}
              visibleCount={visibleCount}
              colors={palette}
              percentageClassName={percentageClassName}
              legendDropdownClassName={legendDropdownClassName}
              onItemSelect={onItemSelect}
            />
            {rightColumn && (
              <button
                type="button"
                onMouseDown={(event) => startResize(1, event)}
                className="hidden lg:block absolute top-0 right-0 h-full w-2 cursor-col-resize bg-transparent hover:bg-slate-200/60"
                aria-label="Resize legend panel"
              />
            )}
          </div>
            {rightColumn && (
              <div
                className="w-full min-w-0 flex flex-col"
                style={isLargeScreen ? { width: `${columnSizes[2]}%` } : undefined}
              >
                {rightColumn}
                {filterMessage}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="w-full h-[400px] flex justify-center items-center">
          <div className="w-20 h-20 border-4 border-dashed rounded-full animate-spin-slow border-ktx-purple flex justify-center items-center"></div>
        </div>
      )}
      {showUnpricedModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-6">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Unpriced customers</div>
                <div className="text-xs text-slate-500">Needs more information</div>
              </div>
              <button
                type="button"
                onClick={() => setShowUnpricedModal(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close unpriced categories"
              >
                ✕
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto p-4 text-xs text-slate-600">
              {unpricedLegendItems.length ? (
                <div className="space-y-2">
                  {unpricedLegendItems.map((item) => (
                    <div key={item.name} className="flex items-center justify-between gap-3">
                      <div className="truncate text-slate-800 font-medium">{item.name}</div>
                      <div className="text-right whitespace-nowrap text-slate-500">
                        {defaultValueFormatter(item.value)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-400">No unpriced categories found.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
